import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App, type URLOpenListenerEvent } from '@capacitor/app';

/**
 * Extract a navigable path from an app link URL.
 *
 * Supports two URL formats:
 *   - HTTPS App Link: `https://www.shareshack.co.uk/groups/abc?item=xyz`
 *   - Custom scheme fallback: `uk.co.shareshack://groups/abc?item=xyz`
 *
 * Returns the pathname + search string (e.g. `/groups/abc?item=xyz`) or
 * `null` if the URL doesn't match either format.
 * @param rawUrl
 */
function extractPath(rawUrl: string): string | null {
  // Custom scheme URLs: uk.co.shareshack://path — `new URL` treats
  // everything after `://` as the pathname (no hostname).
  if (rawUrl.startsWith('uk.co.shareshack://')) {
    const pathAndQuery = rawUrl.slice('uk.co.shareshack:/'.length);
    return pathAndQuery || '/';
  }

  try {
    const url = new URL(rawUrl);
    if (url.hostname === 'www.shareshack.co.uk') {
      return url.pathname + url.search;
    }
  } catch {
    // Malformed URL — fall through
  }

  return null;
}

/**
 * Listens for Android App Link opens on native platforms and navigates
 * to the corresponding React Router route.
 *
 * Handles both warm-start (app already running) and cold-start (app launched
 * by tapping a link) scenarios. Only attaches listeners on native platforms;
 * no-ops on web. The listener is attached at most once via a ref guard.
 *
 * Supports HTTPS App Links (`www.shareshack.co.uk`) and the custom scheme
 * fallback (`uk.co.shareshack://`) for OEMs where App Links verification fails.
 *
 * Must be rendered inside `<BrowserRouter>` (requires `useNavigate`).
 * Mount exactly once via `PushNotificationProvider`.
 */
export function useAppLinks() {
  const navigate = useNavigate();
  const listenerAttached = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || listenerAttached.current) return;
    listenerAttached.current = true;

    // Handle URLs opened while app is running (warm start)
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const path = extractPath(event.url);
      if (path) navigate(path);
    });

    // Handle cold-start URL (app was not running when link was tapped)
    App.getLaunchUrl().then((result) => {
      if (result?.url) {
        const path = extractPath(result.url);
        if (path) navigate(path);
      }
    });
  }, [navigate]);
}
